<template>
  <o-modal v-if="!page.isLoading" @close="closeModalSwitch">
    <template v-slot:header>
      {{ page.cardTitle }}
    </template>
    <template v-slot:body>
      <o-form autocomplete="off">

        <div class="form-input" v-if="access.includes('corporate')">
          <o-form-group title="Corporate" name="corporate" class="form-group-input">
            <o-select v-model="selectedBusinessProfile.corporate_id" autocomplete="'off'"
              :options="corporates"></o-select>
          </o-form-group>

          <o-button :variant="'danger'" class="nav-item btn btn-primary btn-sm ms-3 btn-custom text-white clear-btn" @click="clearSelect('corporate')"
            :type="'button'">Clear</o-button>
        </div>

        <div class="form-input" v-if="access.includes('branch') && selectedBusinessProfile.corporate_id" :class="{ 'required-highlight': showBranchWarning }">
          <o-form-group title="Branch" name="branch" class="form-group-input">
            <o-select v-model="selectedBusinessProfile.branch_id" autocomplete="'off'" :options="branches"></o-select>
            <div v-if="showBranchWarning" class="required-text-hint">
              <i class="fas fa-exclamation-circle me-1"></i> Please select a branch to continue
            </div>
          </o-form-group>

          <o-button :variant="'danger'" class="nav-item btn btn-primary btn-sm ms-3 btn-custom text-white clear-btn" @click="clearSelect('branch')"
            :type="'button'">Clear</o-button>
        </div>

        <div class="form-input" v-if="access.includes('merchant') && selectedBusinessProfile.branch_id && requiredLevel !== 'branch'" :class="{ 'required-highlight': showMerchantWarning }">
          <o-form-group title="Merchant" name="merchant" class="form-group-input">
            <o-select v-model="selectedBusinessProfile.merchant_id" autocomplete="'off'"
              :options="merchants"></o-select>
            <div v-if="showMerchantWarning" class="required-text-hint">
              <i class="fas fa-exclamation-circle me-1"></i> Please select a merchant to continue
            </div>
          </o-form-group>

          <o-button :variant="'danger'" class="nav-item btn btn-primary btn-sm ms-3 btn-custom text-white clear-btn" @click="clearSelect('merchant')"
            :type="'button'">Clear</o-button>
        </div>

        <o-button type="submit" :size="'block'" :variant="'info'" class="switch-btn" :disabled="isSwitchDisabled"
          @click="modalSwitchAction">Switch</o-button>
      </o-form>
    </template>
  </o-modal>
  
 
  <div class="spinner" v-if='page.isLoading'>
    <div class="spinner-border text-primary" role="status">
      <span class="sr-only">Loading...</span>
    </div>
  </div>
</template>

<script>
import {
  OForm,
  OFormGroup,
  OSelect,
  OButton,
  OModal,
} from "@/components/OmnihControl";
import { reactive, ref, computed } from "vue";
import { useStore } from "vuex";
import { onMounted, watch } from "@vue/runtime-core";
import { useRouter } from "vue-router";
import { useTheme } from "@/composables/useTheme";

export default {
  name: "Switch",
  props: {
    targetRoute: {
      type: String,
      default: "Home"
    },
    requiredLevel: {
      type: String,
      default: ""
    }
  },
  components: {
    OForm,
    OFormGroup,
    OSelect,
    OButton,
    OModal,
  },
  setup(props, context) {
    const store = useStore();

    const router = useRouter();

    let page = reactive({
      cardTitle: "Switch Business Profile",
      isLoading: false,
    });

    const selectedBusinessProfile = reactive({
      corporate_id: null,
      branch_id: null,
      merchant_id: null,
      corporate_name: null,
      branch_name: null,
      merchant_name: null
    });

    const user = ref();

    const access = ref([]);

    const corporates = ref([]);

    const branches = ref([]);

    const merchants = ref([]);

    const business_profiles = ref();

    const isSwitchDisabled = computed(() => {
      if (props.requiredLevel === 'merchant') {
        return !selectedBusinessProfile.merchant_id;
      }
      if (props.requiredLevel === 'branch') {
        return !selectedBusinessProfile.branch_id;
      }
      return false;
    });

    const showBranchWarning = computed(() => {
      if (!props.requiredLevel) return false;
      return !selectedBusinessProfile.branch_id;
    });

    const showMerchantWarning = computed(() => {
      if (props.requiredLevel !== 'merchant') return false;
      return !!selectedBusinessProfile.branch_id && !selectedBusinessProfile.merchant_id;
    });

    onMounted(async () => {
      page.isLoading = true;
      await store.dispatch("Auth/read").then(async (result) => {
        business_profiles.value = { ...store.state.Auth.businessProfiles };

        selectedBusinessProfile.corporate_id = await result.corporate_id;
        selectedBusinessProfile.branch_id = await result.branch_id;
        selectedBusinessProfile.merchant_id = await result.merchant_id;

        corporates.value = [
          ...business_profiles.value.corporate.map((v) => ({
            value: v.id,
            text: v.name,
          })),
        ];

        await fetchTheme(selectedBusinessProfile.corporate_id);

        if (selectedBusinessProfile.corporate_id) {
          const corporate = corporates.value.find(v => v.value === selectedBusinessProfile.corporate_id);
          if (corporate) {
            selectedBusinessProfile.corporate_name = corporate.text;
            localStorage.setItem('selected_corporate_name', corporate.text);
          }

          const branch_list =
            business_profiles.value.corporate.find(
              (v) => v.id == selectedBusinessProfile.corporate_id
            ).branch ?? [];

          branches.value = branch_list.map((v) => ({
            value: v.id,
            text: v.name,
            status: v.deleted_at ? 'Inactive' : 'Active'
          }));

          if (selectedBusinessProfile.branch_id) {
            const branch = branches.value.find(v => v.value === selectedBusinessProfile.branch_id);
            if (branch) {
              selectedBusinessProfile.branch_name = branch.text;
              localStorage.setItem('selected_branch_name', branch.text);
            }

            const merchant_list = branch_list
              .find((v) => v.id == selectedBusinessProfile.branch_id)
              .merchant ?? [];

            merchants.value = merchant_list.map((v) => ({              
              value: v.id,
              text: v.name,
              status: v.deleted_at ? 'Inactive' : 'Active'
            }));

            if (selectedBusinessProfile.merchant_id) {
              const merchant = merchants.value.find(v => v.value === selectedBusinessProfile.merchant_id);
              if (merchant) {
                selectedBusinessProfile.merchant_name = merchant.text;
                localStorage.setItem('selected_merchant_name', merchant.text);
              }
            }
          }
        }
        page.isLoading = false;
      });

      user.value = { ...store.state.Auth.currUser };
    });

    const fetchTheme = async (corporateId) => {
      if (corporateId) {
        const { applyTheme } = useTheme();
        await applyTheme(corporateId);
      }
    };

    const closeModalSwitch = () => {
      localStorage.removeItem('selected_corporate_name');
      localStorage.removeItem('selected_branch_name');
      localStorage.removeItem('selected_merchant_name');
      localStorage.removeItem('profile_name');

      context.emit("closeModalSwitch", {reRender: false});
    }

    const modalSwitchAction = (e) => {
      e.preventDefault();

      store.dispatch("Auth/storeBusinessProfile", {
        data: selectedBusinessProfile,
      });

      if(router.currentRoute.value.name == "Home") {
        let data = {};

        if (selectedBusinessProfile.branch_id) {
          data = {branch_id: selectedBusinessProfile.branch_id}
        } else if (selectedBusinessProfile.merchant_id) {
          data = {merchant_id: selectedBusinessProfile.merchant_id}
        }

        if (Object.keys(data).length != 0) {
          store.dispatch('Dashboard/read', data);
        }
      }
    
      localStorage.removeItem('profile_name');

      if (props.targetRoute && props.targetRoute !== 'Home') {
        router.push({ path: props.targetRoute });
      } else {
        router.push({ name: "Home" });
      }
      
      context.emit("closeModalSwitch", {reRender: true});
    };

    const setupAccess = async () => {
      if (user.value.level == "root") {
        access.value = ["corporate", "branch", "merchant"];
      }

      if (user.value.level == "corporate") {
        access.value = ["branch", "merchant"];
        selectedBusinessProfile.corporate_id =
          business_profiles.value.corporate.at(0).id;
      }

      if (user.value.level == "branch") {
        access.value = ["merchant"];
        const corporate = business_profiles.value.corporate.at(0);

        selectedBusinessProfile.corporate_id = corporate.id;
        selectedBusinessProfile.branch_id = await corporate.branch.at(0).id;
      }
    };

    const clearSelect = (el) => {
      if (el == "corporate") {
        selectedBusinessProfile.corporate_id = null;
        selectedBusinessProfile.corporate_name = null;
        
        selectedBusinessProfile.branch_id = null;
        selectedBusinessProfile.branch_name = null;
        
        selectedBusinessProfile.merchant_id = null;
        selectedBusinessProfile.merchant_name = null;
        
        localStorage.removeItem('selected_corporate_name');
        localStorage.removeItem('selected_branch_name');
        localStorage.removeItem('selected_merchant_name');

        branches.value = [];
        merchants.value = [];
      }

      if (el == "branch") {
        selectedBusinessProfile.branch_id = null;
        selectedBusinessProfile.branch_name = null;
        
        selectedBusinessProfile.merchant_id = null;
        selectedBusinessProfile.merchant_name = null;
        
        localStorage.removeItem('selected_branch_name');
        localStorage.removeItem('selected_merchant_name');
        
        merchants.value = [];
      }

      if (el == "merchant") {
        selectedBusinessProfile.merchant_id = null;
        selectedBusinessProfile.merchant_name = null;
        localStorage.removeItem('selected_merchant_name');
      }
    };

    watch(
      () => user.value,
      () => {
        setupAccess();
      }
    );

    watch(
      () => selectedBusinessProfile.corporate_id,
      async (newVal) => {
        merchants.value = [];
        selectedBusinessProfile.branch_id = null;
        selectedBusinessProfile.merchant_id = null;

        localStorage.removeItem('selected_merchant_name');
        localStorage.removeItem('selected_corporate_name');
        localStorage.removeItem('selected_branch_name');

        const corporate = business_profiles.value.corporate.find(
          (v) => v.id == newVal
        );

        if (corporate) {
          selectedBusinessProfile.corporate_name = corporate.name;
          localStorage.setItem('selected_corporate_name', corporate.name);

          branches.value = [
            ...corporate.branch.map((v) => ({
              value: v.id,
              text: v.name,
              status: v.deleted_at ? 'Inactive' : 'Active'
            })),
          ];
        }

        await fetchTheme(newVal);
      }
    );

    watch(
      () => selectedBusinessProfile.branch_id,
      async (newVal) => {
        selectedBusinessProfile.merchant_id = null;
        localStorage.removeItem('selected_merchant_name');
        localStorage.removeItem('selected_corporate_name');
        localStorage.removeItem('selected_branch_name');

        const corporate = business_profiles.value.corporate.find(
          (v) => v.id == selectedBusinessProfile.corporate_id
        );

        if (corporate) {
          const branch = corporate.branch.find((v) => v.id == newVal);

          if (branch) {
            selectedBusinessProfile.branch_name = branch.name;
            localStorage.setItem('selected_branch_name', branch.name);

            merchants.value = [
              ...branch.merchant.map((v) => ({
                value: v.id,
                text: v.name,
                status: v.deleted_at ? 'Inactive' : 'Active'
              })),
            ];
          }
        }
        await fetchTheme(selectedBusinessProfile.corporate_id);
      }
    );

    watch(
      () => selectedBusinessProfile.merchant_id,
      async (newVal) => {
        if (newVal) {
          const merchant = merchants.value.find(v => v.value === newVal);
          if (merchant) {
            selectedBusinessProfile.merchant_name = merchant.text;
            localStorage.removeItem('selected_corporate_name');
            localStorage.removeItem('selected_merchant_name');
            localStorage.removeItem('selected_branch_name');
            localStorage.setItem('selected_merchant_name', merchant.text);
          }
        }
        await fetchTheme(selectedBusinessProfile.corporate_id);
      }
    );

    return {
      page,
      access,
      user,
      selectedBusinessProfile,
      corporates,
      branches,
      merchants,
      setupAccess,
      business_profiles,
      clearSelect,
      modalSwitchAction,
      closeModalSwitch,
      fetchTheme,
      isSwitchDisabled,
      showBranchWarning,
      showMerchantWarning,
    };
  },
};
</script>

<style scoped>
.form-group {
  text-align: left;
}

.form-input {
  display: flex;
  justify-content: space-between;
}

.required-highlight :deep(select),
.required-highlight select {
  border-color: var(--primary-color, #F93E3E) !important;
  box-shadow: 0 0 0 0.2rem rgba(249, 62, 62, 0.15) !important;
}

.required-text-hint {
  color: var(--primary-color, #F93E3E);
  font-size: 0.75rem;
  margin-top: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

@media screen and (min-width: 768px) {
  .form-input {
    min-width: 430px;
  }
}

.form-group-input {
  width: 80%;
}

.clear-btn {
  margin-top: 21px;
  padding: 11px;
}

.switch-btn {
  margin-top: 20px;
  background-color: var(--primary-color, #F93E3E) !important;
  border-color: var(--primary-color, #F93E3E) !important;
  color: #ffffff !important;
  transition: all 0.2s ease;
}

.switch-btn:hover {
  background-color: var(--primary-color, #F93E3E) !important;
  border-color: var(--primary-color, #F93E3E) !important;
  color: #ffffff !important;
  filter: brightness(85%);
}

.btn-custom-switch {
  padding: 0px;
  color: var(--primary-color, #4aae9b);
  position: absolute;
  top: 12px;
  left: 92%;
}

.btn-custom-switch:focus {
  outline: none;
  border: none;
  box-shadow: none;
}

.btn-custom-switch:hover {
  color: var(--secondary-color, red);
}

.spinner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 9999;
}

@media (max-width: 768px) {
  .btn-custom-switch {
    left: 87%;
  }
  
}
</style>